import { Injectable } from "@angular/core";
import { MessageThreadFilter } from "../shared/models/app-message.models";

export type IEContributionRoute =
    | "MonetaryContribution"
    | "InKindContributions"
    | "RefundTransactions"
    | "IE";

@Injectable()
export class MasterUrlService {
    getNotificationInstance(filerId: number, notificationId: number): string {
        return `api/messaging/${filerId}/notification/${notificationId}`;
    }

    getAccessEthicsFiledReports(filerId: number): string {
        return `api/Filing/PreviousAccessEthicsReportsByFilerId?filerId=${filerId}`;
    }

    getUnreadNotificationCount(filerId: number = 0): string {
        return !filerId
            ? "api/Messaging/unread/admin"
            : `api/Messaging/unread/${filerId}`;
    }

    checkContributionLimits(data: {
        filerId: number;
        contactId: number;
        contributionAmount: number;
        contactTypeId: number;
        previousValue?: number;
        refundAmount?: number;
        previousRefundAmount?: number;
        transactionDate: string;
        fairElections?: boolean;
        electionCycleId: number;
    }): string {
        const options = Object.entries(data)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${key}=${value}`)
            .join("&");

        return `${this.checkContributionLimit}?${options}`;
    }

    getMessageThreadsOf(
        id: string | number,
        filter: MessageThreadFilter
    ): string {
        const _ = new URLSearchParams();

        Object.entries(filter)
            .filter(([key, value]) => !!value)
            .forEach(([key, value]) => _.append(key, value));

        const queryParams = _.toString();

        return `api/messaging/${id}${
            queryParams.length > 0 ? `?${queryParams}` : ""
        }`;
    }

    blobAttachment = "api/Attachment";
    user = "api/UserManagement/user";
    storageDocuments = "api/Attachment/Storage?fileType=";

    // API Token getting details
    clientId = "ro.client";
    scope = "api1";

    private applyParams(route: string, bundle: any): string {
        const params = Object.entries(bundle)
            .map(([key, value]) => `${key}=${value}`)
            .join("&");
        return `${route}?${params}`;
    }

    //User Management
    deleteFilerEntityUser = (_: {
        userId: string | number;
        entityId: number;
        filerTypeId: number;
    }): string =>
        this.applyParams("api/UserManagement/DeleteFilerEntityUser", _);

    requestAffiliation = "api/filer/affiliation";
    getFilerEntitiesByType = "api/filer/type";

    getFilerTypes = "api/LookUp/getLookUps?lookUpTypeCode=";
    committeeInformation = "api/committee/createDetails";

    validateAspNetUserEmailCheck =
        "api/UserManagement/ValidateAspNetUserEmailCheck?UserEmailID=";

    validateFilerName = "api/UserManagement/ValidateFilerName";

    getRegistrationDetails(entityId: number = 0, filerTypeId: number = 0) {
        return `api/UserManagement/GetRegistrationDetails?entityId=${entityId}&filerTypeId=${filerTypeId}`;
    }

    //Public
    getCandidatesElectionCycleList =
        "api/LookUp/getCandidatesElectionCycleList";
    getElectionCyclesByFiler = "api/Lookup/getElectionCyclesByFiler?filerId=";
    getCandidateGraph(
        electionCycleId: number = 0,
        officeId: number = 0,
        districtId = 0
    ) {
        return `api/Committee/GetCandidateGraphByElectionCycleAndOffice?electionCycleId=${electionCycleId}&officeId=${officeId}&districtId=${districtId}`;
    }

    getFileExtensionWhiteList = "api/LookUp/GetFileExtensionWhiteList";
    //calendar
    saveElectionCycle = "api/Calendar/saveElectionCycle";
    deleteElectionCycle = "api/Calendar/deleteElectionCycle?electionCycleId=";
    getElectionCycles = "api/Calendar/getElectionCycles";
    getCandidateElectionCycles =
        "api/Calendar/getElectionCycles/type=Candidate";
    saveEvents = "api/Calendar/SaveEvents";
    deleteEvents = "api/Calendar/DeleteEvents?eventId=";
    getEvents = "api/Calendar/GetEvents";
    saveFilingPeriod = "api/Calendar/SaveFilingPeriod";
    deleteFilingPeriod = "api/Calendar/DeleteFilingPeriod";
    getFilingPeriods = "api/Calendar/GetFilingPeriods";
    getFilingPeriodsById = "api/Calendar/GetFilingPeriodById?filingperiodid=";
    getEventsById = "api/Calendar/GetEventsById?eventId=";
    getCityItemFilingPeriods = "api/CityItem/FilingPeriods";
    getUserEntitiesDetails = "api/UserManagement/getUserEntityDetails";

    //Calendar API's
    getCalendarList = "api/Calendar/getCalendarEvents?";
    getPublicCalendarList = "api/Calendar/getPublicCalendarEvents?";
    getFilerCalendarList = "api/Calendar/getFilerCalendarEvents?";
    downloadCalendar = "api/Calendar/DownloadCalendar";
    getExpenditures = "api/Filing/getTransactionsByFiler";

    // Transaction
    saveContribution = "api/Transaction/saveContribution";
    saveUnitemizedTotal = "api/Transaction/saveUnitemizedTotal";
    downloadUploadTransactionsTemplate =
        "api/transaction/DownloadUploadTransactionsTemplate";
    uploadTransactionsTemplate = "api/transaction/uploadTransaction";
    getMonetaryContributionsByFiler =
        "api/transaction/GetMonetaryContributionTransactionsByFiler";
    getMonetaryContributionTransactionsByFiler =
        "api/transaction/monetary-contributions/filer/";
    getInKindContributionsByFiler =
        "api/transaction/GetInKindContributionTransactionsByFiler";
    getInKindContributions = "api/transaction/in-kind-contributions/filer/";
    getFairElectionsFundingTransactionsByFiler =
        "api/transaction/GetFairElectionsFundingTransactionsByFiler";
    getFefTransactionsByFiler = "api/transaction/fef-transactions/filer/";
    getRefundTransactionsByFiler =
        "api/transaction/GetRefundTransactionsByFiler";
    getRefundsByFiler = "api/transaction/refunds/filer/";
    getExpenditureTransactionsByFiler =
        "api/transaction/GetExpenditureTransactionsByFiler";
    getExpendituresByFiler = "api/transaction/expenditure-transactions/filer/";
    getUnpaidObligationTransactionsByFiler =
        "api/transaction/GetUnpaidObligationTransactionsByFiler";
    getUnpaidObligationsByFiler = "api/transaction/unpaid-obligations/filer/";
    getFEFQualifyingContributionTransactionsByFiler =
        "api/transaction/GetFEFQualifyingContributionTransactionsByFiler";
    getFefQualifyingContributionTransactionsByFiler =
        "api/transaction/fef-contributions/filer/";
    getMonetaryContributionUnitemizedTotal =
        "api/transaction/getCampaignFinanceReportMonetaryContributionUnitemizedTotalByFilerAndFilingPeriod";
    getInKindContributionUnitemizedTotal =
        "api/transaction/getCampaignFinanceReportInKindContributionUnitemizedTotalByFilerAndFilingPeriod";
    getExpendituresUnitemizedTotal =
        "api/transaction/getCampaignFinanceReportExpenditureUnitemizedTotalByFilerAndFilingPeriod";
    deleteTransaction = "api/transaction/DeleteTransaction?transactionid=";
    deleteTransactionRefund =
        "api/transaction/DeleteTransactionRefund?parentTransactionId=";
    saveExpenditure = "api/Transaction/saveExpenditure";
    GetFilingSearchTransactions = "api/Transaction/GetFilingSearchTransactions";

    //FEF Withdrawal
    getAdminFEFWithdrawalInfoByFiler =
        "api/FEFWithdrawal/getAdminFEFWithdrawalQualifyForPublicFundingUserEntityDetailsByFiler?filerId=";
    getFEFWithdrawalInfoByFiler =
        "api/FEFWithdrawal/getFEFWithdrawalQualifyForPublicFundingUserEntityDetailsByFiler?filerId=";
    saveFEFWithdrawalQualifyForPublicFunding =
        "api/FEFWithdrawal/SaveFEFWithdrawalQualifyForPublicFunding";
    saveFEFWithdrawalQualifyForPublicFundingRecordDetail =
        "api/FEFWithdrawal/SaveFEFWithdrawalQualifyForPublicFundingRecordDetail";

    // FEF Qualifying-contributions
    linkFEFDisbursementWithContributions =
        "api/FEFQualifyingContributions/linkFEFDisbursementWithContributions";
    saveFEFDisbursement = "api/FEFDisbursement/";
    getFEFDisbursementInfoByFiler = "api/FEFDisbursement/";
    deleteFEFDisbursement = "api/FEFDisbursement/";

    //FEFApplication
    getFEFQualifyingContributions = "api/FEFQualifyingContributions/";
    getFEFReport = "api/FEFQualifyingContributions/report?filingId=";
    getFEFReportPDF = "api/FEFQualifyingContributions/PDFreport?filingId=";

    getFEFApplicationFilingInfoUserDetailsByFiler(
        filerId: number
    ) {
        return `api/FEFApplication/getFEFApplicationQualifyingContributionFilingInfoUserEntityDetailsByFiler?filerId=${filerId}`;
    }

    getFEFApplicationFilingInfoUserDetailsByFilerAndFilingId(
        filerId: number,
        filingId: number
    ) {
        return `api/FEFApplication/getFEFApplicationQualifyingContributionFilingInfoUserEntityDetailsByFiler?filerId=${filerId}&filingId=${filingId}`;
    }
    getFEFApplicationSummaryInfo =
        "api/FEFApplication/getFEFApplicationSummaryInfoByFiler?filerId=";
    getFEFApplicationSummaryInfoSubmitted =
        "api/FEFApplication/getFEFApplicationSummaryInfoByFilerSubmitted?filerId=";
    getFEFApplicationTransactionsByFiler =
        "api/FEFApplication/getFEFApplicationTransactionsByFiler?filerId=";
    saveFEFApplicationQualifyForPublicFunding =
        "api/FEFApplication/SaveFEFApplicationQualifyForPublicFunding";
    GetFEFApplicationSummaryInfoByFiling =
        "api/FEFApplication/getFEFApplicationSummaryInfoByFiling?filingId=";
    GetFEFApplicationTransactionsByFiling =
        "api/FEFApplication/getFEFApplicationTransactionsByFiling?filingId=";
    //FEF Qualifying Contributions
    GetFEFQualifyingContributions = "api/FEFQualifyingContributions/";
    GetFEFQualifyingContributionsDownload =
        "api/FEFQualifyingContributions/download";

    //FEF FEFTransactionStatus
    updateFEFTransactionStatus = "api/FEFTransactionStatus/";

    //Admin manage users
    getAdministratorUser = "api/UserManagement/getCurrentAdministratorUserList";
    getFilerDetailsByName = "api/UserManagement/GetFilerDetailsByName";
    adminInviteUser = "api/UserManagement/AdminInviteUser";
    addNewAdministratorAspNetUser =
        "api/UserManagement/AddNewAdministratorAspNetUser";
    revokeAdministratorUserAccess =
        "api/UserManagement/RevokeAdministratorUserAccess";
    updateUserStatus = "api/UserManagement/UpdateUserStatus";
    getUserList = "api/UserManagement/UserList";
    getManageUsers = "api/UserManagement/getManageUsers";
    getApplicationUserStatus = "api/LookUp/getApplicationUserStatusesList";
    approveFilerFEFParticipationStatus = "api/fefparticipationstatus/approve";
    revokeFilerFEFParticipationStatus = "api/fefparticipationstatus/revoke";
    denyFilerFEFParticipationStatus = "api/fefparticipationstatus/deny";
    //Filing
    changePublicationStatus = "api/Filing/PublicationStatus";
    getTransactionById = "api/filing/getTransactionDetailsById";
    GetTransactionDetailsById = "api/Filing/GetTransactionDetailsById?txnId=";
    getFilingInfo = "api/Filing/getFilingInfo";
    SubmitFiling = "api/Filing/SaveFilingCampaignFinanceReport";
    SaveSpecialFilingCampaignFinanceReport =
        "api/Filing/SaveSpecialFilingCampaignFinanceReport";
    SubmitContributionReport =
        "api/Filing/SaveFillingQualifyingContributionReport";
    updateFilingStatus = "api/Filing/UpdateFilingStatus";
    updateFilingNoteOrStatus = "api/Filing/UpdateFilingNoteOrStatus";
    updateFilingNote = "api/Filing/UpdateFilingNote";
    getSummaryInfo = "api/Filing/GetSummaryInfo";
    getSummaryInfoByFiling = "api/filing/GetSummaryInfoByFiling";
    getSummaryInfoByFiler = "api/filing/summary/filer/";
    getTransactionsByFiler = "api/Filing/getTransactionsByFiler";
    getMonetaryContributionsByFiling =
        "api/transaction/GetMonetaryContributionTransactionsByFiling";
    getInKindContributionsByFiling =
        "api/transaction/GetInKindContributionTransactionsByFiling";
    getFEFQualifyingContributionTransactionsByFiling =
        "api/transaction/GetFEFQualifyingContributionTransactionsByFiling";
    getFairElectionsFundingTransactionsByFiling =
        "api/transaction/GetFairElectionsFundingTransactionsByFiling";
    getRefundTransactionsByFiling =
        "api/transaction/GetRefundTransactionsByFiling";
    getExpenditureTransactionsByFiling =
        "api/transaction/GetExpenditureTransactionsByFiling";
    getCampaignFinanceReportExpenditureUnitemizedTotalByFiling =
        "api/transaction/GetCampaignFinanceReportExpenditureUnitemizedTotalByFiling";
    getCampaignFinanceReportMonetaryContributionUnitemizedTotalByFiling =
        "api/transaction/GetCampaignFinanceReportMonetaryContributionUnitemizedTotalByFiling";
    getCampaignFinanceReportInKindContributionUnitemizedTotalByFiling =
        "api/transaction/GetCampaignFinanceReportInKindContributionUnitemizedTotalByFiling";
    addFillingNote = "api/filing/SaveFilingMappingRecordNote";
    getUserEntityDetailsByFiling =
        "api/UserManagement/GetUserEntityDetailsByFiling";
    getCampaignFinanceReportFilingInfoByFiler =
        "api/filing/getCampaignFinanceReportFilingInfoByFiler?filerId=";
    getCampaignFinanceReportFilingInfoByFiling =
        "api/filing/information/filing/";
    getCampaignFinanceReportCommitteeInfoByFiling =
        "api/filing/committee-information/filing/";
    getCampaignFinanceReportPDFDownload = "api/Filing/PDFReport?";
    getSpecialCampaignFinanceReportPDFDownload = "api/Filing/special-report/";
    //Upload
    uploadSave = "api/Attachment/upload";
    uploadSaveAttachments = "api/Attachment/SaveAttachments";
    uploadRemove = "api/Attachment/delete";

    // Filings
    GetReviewFilingsCampaignFinanceReport =
        "api/Filing/GetReviewFilingsCampaignFinanceReport";
    getReviewFilingsCampaignFinanceReportByFilerIdbyGroupFilings(
        filerId: number | null,
        filingTypeId: number = 0
    ) {
        if (filingTypeId > 0) {
            return `api/Filing/ReviewFilingCampaignFinanceReport/Filer/?filerId=${filerId}&filingTypeId=${filingTypeId}`;
        }
        return `api/Filing/ReviewFilingCampaignFinanceReport/Filer/?filerId=${filerId}`;
    }
    getCampaignFilingByCommittee =
        "api/Filing/GetCampaignFilingByCommittee/Committee/?committeeId=";
    getReviewFilingsLateReport = "api/Filing/GetReviewFilingsLateReport";
    getReviewFilingsRepeatLateOffendersReport =
        "api/Filing/GetReviewFilingsRepeatLateOffendersReport";
    getReviewFilingsEthicsReport = "api/Filing/GetReviewFilingsEthicsReport";
    downloadEthicsBoard = "api/filing/EthicsBoard";
    getFEFParticipationStatusByFiling =
        "api/Filing/GetFEFParticipationStatusByFiling";

    //Access Ethics
    getAccessEthicsByFilerId =
        "api/AccessEthics/GetAccessEthicsByFilerId?filerId=";
    GetAccessEthicsById = "api/AccessEthics/GetAccessEthicsById";
    saveAccessEthicsRegistration =
        "api/AccessEthics/saveAccessEthicsRegistration";
    saveEconomicInterestFiler = "api/AccessEthics/economic-interest";
    getEconomicInterestRoles = "api/AccessEthics/roles/";
    editEconomicInterestRoles = "api/AccessEthics/roles";

    getFEFParticipationStatusTypesList =
        "api/LookUp/getFEFParticipationStatusTypesList";
    SaveFEFIntentQualifyForPublicFundingRecordNoteAndStatus =
        "api/FEFIntent/SaveFEFIntentQualifyForPublicFundingRecordNoteAndStatus";

    getContactInformationByFiler =
        "api/FinancialDisclosure/contact-information/filer/";
    getContactInformationByFiling =
        "api/FinancialDisclosure/contact-information/filing/";
    getFinancialDisclosureReport = "api/FinancialDisclosure/report";
    submitFinancialDisclosureReport = "api/FinancialDisclosure/submit";
    saveFinancialDisclosureReport = "api/FinancialDisclosure/save";
    getReportTypesByFiler = "api/FinancialDisclosure/getReportTypesByFiler";

    getCurrentFEFParticipationStatus = "api/fefparticipationstatus/current";
    GetFairElectionFundingByFilerId =
        "api/filing/getFairElectionFundingByFilerId?filerId=";
    getFEFIntentQualifyForPublicFundingUserEntityDetailsByFilerId =
        "api/FEFIntent/getFEFIntentQualifyForPublicFundingUserEntityDetailsByFiler?filerId=";
    saveIntent = "api/FEFIntent/SaveFEFIntentQualifyForPublicFunding";
    verifyFEFAddress = "api/FEFAddressVerification/VerifyAddress";

    //TransactionSearch
    SearchExpenditureTransactions =
        "api/Transaction/SearchExpenditureTransactions";
    SearchContributionTransactions =
        "api/Transaction/SearchContributionTransactions";
    SearchPenalties = "api/Transaction/SearchPenalties";

    //Transactions
    updateTransactionAdminNotes = "api/Transaction/notes/admin/";

    //AdminDocuments
    adminDocumentsApi = "api/AdminDocument";
    adminDocumentsViewer = "api/AdminDocument/Viewer?adminDocumentId=";
    adminDocumentsByFilerIdApi = "api/AdminDocument/ByFilerId";
    getComplaintStatuses = "api/AdminDocument/GetComplaintStatuses";
    getAdminDocTypes = "api/AdminDocument/GetAdminDocTypes";

    //Filer
    filerApi = "api/Filer";
    getFilerById = "api/Filer/filer/";
    filingSnapshot = "api/filer/snapshot";
    terminateFiler = "api/Filer/terminate";

    //FilerTypes
    IE = 2;
    Committee = 3;
    EthicsFiler = 4;

    // Local Storage Keys
    UserFilers = "userfilers";
    AdminSelectedUserFilers = "adminSelectedUserFiler";
    PublicSelectedUserFilers = "publicSelectedUserFiler";
    UserEntity = "userEntity";
    UserData = "UserData";
    CurrentCommittee = "CurrentCommittee";

    //Election Type
    Candidate = 1;
    Issue = 2;
    Special = 3;

    //Election Type
    ElectionCycleStatusActive = 1;
    ElectionCycleStatusCompleted = 2;
    ElectionCycleStatusCanceled = 3;

    getElectionCyclesFromCommitteeByFiler =
        "api/LookUp/getElectionCyclesFromCommitteeByFiler?filerId=";
    dashboardItems = "api/LookUp/dashboard-items";

    //Committee
    committeeByName = "api/Committee/GetCommitteeByName";
    SearchCommitteeByName =
        "api/Committee/SearchCommitteeByName?searchCommittee=";
    getAllCandidate = "api/Committee/getAllCandidate?search=";

    getRejectedAffiliationRequests = (_: {
        entityId: number;
        filerTypeId: number;
        filerId: number;
    }): string =>
        this.applyParams(
            "api/UserManagement/GetRejectedAffiliationRequests",
            _
        );

    getAffiliationRequests = (_: {
        entityId: number;
        filerTypeId: number;
        filerId: number;
    }): string =>
        this.applyParams("api/UserManagement/GetAffiliationRequests", _);

    getPendingInvitationsByFiler = (_: { filerId: number }): string =>
        this.applyParams("api/UserManagement/GetPendingInvitationsByFiler", _);

    getInvitationDetails = (_: { token: string }): string =>
        this.applyParams("api/UserManagement/GetInvitationDetails", _);

    resolveInvitation = "api/UserManagement/ResolveInvitation";
    setAsPrimary = "api/UserManagement/MakePrimaryUser";
    adminSetAsPrimary = "api/UserManagement/AdminSetAsPrimaryUser";

    getFilerDashboardTotals = "api/committee/Totals";
    getCurrentCommitteeByFiler = "api/committee/getcurrentbyfiler";
    getCurrentCommitteeFefstatusIdByFiler =
        "api/committee/GetCurrentCommitteeFefstatusIdByFiler";
    approveDenyAffiliation = "api/UserManagement/ApproveDenyAffiliation";
    editCommittee = "api/Committee/EditCommittee";
    getCommitteeUserTypeList = "api/LookUp/getCommitteeUserTypeList";
    GetCommitteeRegistrationById =
        "api/Committee/GetCommitteeRegistrationById/";
    GetCurrentByFiler = "api/Committee/GetCurrentByFiler?filerId=";

    //Contact

    editContactRedactionStatus = "api/Contact/redaction-status";
    getAllContacts = "api/Contact";
    getContacts = "api/Contact/getContacts?name=";
    searchContacts = "api/Contact/SearchContacts";
    getContactDetailsById = "api/Contact/getContactDetailsById?contactId=";
    getCandidatesByElectionCycle =
        "api/contact/GetCandidatesByElectionCycle?electionCycleId=";
    getOpposingCandidates(
        electionCycleId: number,
        filerId: number,
        office: string,
        district: string
    ) {
        return `api/contact/GetOpposingCandidates?electionCycleId=${electionCycleId}&filerId=${filerId}&office=${office}&district=${district}`;
    }
    editContact = "api/contact/EditContact";
    editContactRecordStatusId = "api/contact/editContactRecordStatusId";
    //Committee Office
    getStatelist = "api/common/state/getList";
    getAllOfficeList = "api/LookUp/getLookUps?lookUpTypeCode=OFF";
    getOfficeSearchByName = "api/Committee/GetOfficersListByName?officerName=";
    getCommitteeDetails = "api/Committee/GetCommitteeDetail";
    downloadCSV = "api/Committee/DownloadManageFilerList";
    addNewCommitteeUser = "api/Committee/addNewCommitteeUser";
    // Districts
    getDistrictsList = "api/District/";
    addDistrict = "api/District/";
    deleteDistrict = "api/District/";
    getSupportingContributorsByCommittee(
        filerId: number,
        electionCycleId: number
    ) {
        return `api/Committee/GetSupportingContributorsByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }
    getOpposingContributorsByBallotIssued =
        "api/Committee/GetOpposingContributorsByBallotIssued?ballotIssueId=";
    getSupportingIndependentSpendersByCommittee(
        filerId: number,
        electionCycleId: number,
        positionType: number
    ) {
        return `api/Committee/GetSupportingorOpposingIndependentSpendersByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}&positionType=${positionType}`;
    }
    getOpposingIndependentSpendersByCommittee(
        filerId: number,
        electionCycleId: number,
        positionType: number
    ) {
        return `api/Committee/GetSupportingorOpposingIndependentSpendersByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}&positionType=${positionType}`;
    }

    getExpendituresByCommittee(filerId: number, electionCycleId: number) {
        return `api/Committee/getExpendituresByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }

    getExpendituresTotalByCommittee(filerId: number, electionCycleId: number) {
        return `api/Committee/getExpendituresTotalByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }

    getContributionsTotalByCommittee(filerId: number, electionCycleId: number) {
        return `api/Committee/getContributionsTotalByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }
    getFEFContributionsTotalByCommittee(
        filerId: number,
        electionCycleId: number
    ) {
        return `api/Committee/GetFEFContributionTotalByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }

    getFEFExpendituresTotalByCommittee(
        filerId: number,
        electionCycleId: number
    ) {
        return `api/Committee/getFEFExpendituresTotalByCommittee?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }

    getFinancialOverviewByCandidate(filerId: number, electionCycleId: number) {
        return `api/committee/getFinancialOverviewByCandidate?filerId=${filerId}&electionCycleId=${electionCycleId}`;
    }
    getCashOnHand = "api/committee/GetCashOnHand?filerId=";

    //Committee  to support , Second Committee ,Neighbors Opposed
    updateCommitteeOrLobbyistStatus =
        "api/Committee/UpdateCommitteeorLobbyistStatus";

    //System Management
    getFilerTypeList = "api/SystemManagement/GetFillerTypeList";
    addCommitteeType = "api/SystemManagement/SaveCommitteeType";

    deleteCommitteeType = "api/SystemManagement/DeleteCommitteeType";
    getCommitteeTypeList = "api/SystemManagement/GetCommitteeTypeList";
    validateCommitteeType = "api/SystemManagement/ValidateCommitteeType";
    validateOffice = "api/SystemManagement/ValidateOffice";
    addOffice = "api/SystemManagement/SaveOffice";
    deleteOffice = "api/SystemManagement/DeleteOffice";
    getallOfficeList = "api/SystemManagement/GetAllOfficeList";
    createModifyForm = "api/SystemManagement/CreateModifyFormImageUpload";
    updateModifyForm = "api/SystemManagement/UpdateModifyFormImageUpload";

    getModifyForms = "api/SystemManagement/GetModifyFormImageById";

    checkContributionLimit =
        "api/SystemManagement/ValidateContributionLimitByContactAndElectionCycle";
    getTreasurerDocument = "api/Committee/TreasurerDocument";
    getCandidateAffidavit = "api/Committee/CandidateAffidavit";
    GetCommitteeOfficersList =
        "api/Committee/GetCommitteeOfficersList?committeeid=";
    GetCommitteeDetailsByFiler =
        "api/Committee/GetCommitteeDetailsByFiler?filerId=";
    GetCommitteeDetailsByFilerId = "api/Committee/GetCommitteeDetailsByFiler";
    CommitteeDetail = "api/UserManagement/getUserEntityDetails?entityId=";
    getCurrentUserList = "api/UserManagement/GetFilerUsers";

    sendFilerInvite = "api/UserManagement/FilerInviteUser";
    addUserToFiler = "api/UserManagement/addUserToFiler";

    //manage Committee
    getsearchFilter = "api/UserManagement/getFilerNamesByName";
    getFilerUserNamesByName = "api/UserManagement/getFilerUserNamesByName";

    //UserManagement
    getManageFilerDetail = "api/UserManagement/GetManageFilerDetail";
    getManageFiler = "api/UserManagement/getManageFilers";

    addNewUserAccessEthicsJoinRequest = "api/AccessEthics";
    getLookUps = "api/LookUp/getLookUps?lookUpTypeCode=";

    getLookUpStateList = "api/LookUp/getStatesList";

    getNotificationUserTypesList = "api/LookUp/getNotificationUserTypesList";

    saveEmailNotification = "api/notification/saveEmailNotification";
    deleteNotification = "api/notification/deleteNotification";
    GetTemplates = "api/notification/GetTemplates";
    saveTemplate = "api/notification/saveTemplate";
    deleteTemplate = "api/notification/deleteTemplate?templateId=";
    archivedNotifications = "api/notification/archive";
    notificationEmailTest = "api/email/notificationEmailTest";
    helpEmail = "api/email/helpEmail";
    AdminHelp = "api/Help";
    templateNameExists = "api/notification/TemplateNameExists?name=";

    //Lookups
    getStatesList = "api/LookUp/getStatesList";
    getCityStatesList = "api/LookUp/GetCityStatesList";

    getElectionDates = "api/LookUp/getElectionDates";
    getTheNewElectionDatesByCommittee =
        "api/LookUp/getTheNewElectionDatesByCommittee";
    getApplicationUserStatusesList =
        "api/LookUp/getApplicationUserStatusesList";
    getCommitteeTypesList = "api/LookUp/getCommitteeTypesList";
    getContactRolesList = "api/LookUp/getContactRolesList";
    getContactTypesList = "api/LookUp/getContactTypesList";
    getContributorTypesList = "api/LookUp/getContributorTypesList";
    getElectionCycleStatusesList = "api/LookUp/getElectionCycleStatusesList";
    getElectionTypesList = "api/LookUp/getElectionTypesList";

    getFilerStatusesList = "api/LookUp/getFilerStatusesList";
    getFilerTypesList = "api/LookUp/getFilerTypesList";
    getFilingStatusesList = "api/LookUp/getFilingStatusesList";
    getFilingStatusesListExcludingDraft =
        "api/LookUp/getFilingStatusesListExcludingDraft";

    getOfficeTypesList = "api/LookUp/getOfficeTypesList";

    getPositionTypesList = "api/LookUp/getPositionTypesList";
    getTransactionSubTypesList = "api/LookUp/getTransactionSubTypesList";
    getTransactionTypesList = "api/LookUp/getTransactionTypesList";

    getElectionCycleList = "api/LookUp/getElectionCycleList";

    getCampaignFinanceReportElectionCycleListByFiler =
        "api/LookUp/getCampaignFinanceReportElectionCycleListByFiler?filerId=";
    getCampaignFinanceFilingPeriodsByElectionCycle(
        electionCycleId: number,
        filerId: number
    ) {
        return `api/LookUp/getCampaignFinanceReportFilingPeriodListByElectionCycle?electionCycleId=${electionCycleId}&filerId=${filerId}`;
    }

    //public
    getpublicPenalties = "api/Payments/getpublicPenalties/";
    getPublicComplaints = "api/Committee/getPublicComplaints/";
    getPublicAudits = "api/Committee/getPublicAudits/";

    getMessageByThreadId(filerId: number, threadId: string) {
        return `api/messaging/${filerId}/thread/${threadId}`;
    }

    addNewMessage(filerId: number) {
        return `api/messaging/${filerId}`;
    }

    readonly getAdminUnreadMessages: string = "api/messaging/admin/unread";

    readonly getTemplate: string = "api/messaging/admin/template";

    getAllScheduledNotifications(): string {
        return "api/notification/getemailschedulednotifications";
    }

    getScheduledNotification(id: number): string {
        return `api/notification/getemailnotificationbyid?notificationId=${id}`;
    }

    readonly getSystemDates = "api/notification/getSystemDates";

    getCfrButtonOption = "api/Filing/button-option/";

    saveMajorContributionReport(filerId: number) {
        return `api/filing/SaveFilingMajorContributionReport/${filerId}`;
    }

    getMCRTransactions = "api/transaction/getMCRTransactions?filerId=";

    getMCRTransactionsByFiling = "api/transaction/getMCRTransactionsByFiling";

    checkIfExistElectionCycleActive =
        "api/transaction/CheckIfExistElectionCycleActive";
    editFEFDisbursement = "api/FEFDisbursement/";

    getInitialReportTransactionsByFiler =
        "api/transaction/GetInitialReportTransactionsByFiler/";
    getSpecialSummaryInfo = "api/filing/GetSpecialSummaryInfo/";
    getInitialCampaignFinanceReportDateRange =
        "api/filing/getInitialCampaignFinanceReportDateRange";

    readonly CFGuidanceUrl =
        "https://vote.minneapolismn.gov/candidates/campaign-finance/#:~:text=Submit%20reports%20if%20you%20raise%20or%20spend%20more%20than%20%24750" as const;

    SubmitFilingCertification = "api/Filing/SaveFilingCertificationReport";

    getLatestFinancialDisclosureReportOriginal =
        "api/FinancialDisclosure/original-report";

    downloadCertificationReport(filingId: number) {
        return `api/Filing/certification-filing-pdf/?filingId=${filingId}`;
    }

    checkIfCertificationFilingAvailable =
        "api/filing/certification-filing-available";
    validateSEIEmailCheck =
        "api/UserManagement/ValidateSEIEmailCheck?UserEmailID=";

    getUnresolvedInvitations = "api/UserManagement/GetUnresolvedInvitations";
    resendInvitation = "api/UserManagement/resendInvitation";

    getRecentElectionCyclesAvailable =
        "api/Committee/recent-election-cycles-available?filerId=";

    updateElectionCycle = "api/Committee/change-committee-election-cycle";
}
